import { useEffect, useMemo, useState } from 'react';
import {
  bankCsvApi, invoiceApi, matterApi,
  type BankCsvImport, type BankCsvRow, type InvoiceSummary, type MatterSummary,
} from '../../api';
import Select from '../../components/common/Select';

// CSV bank-import workflow. Only reconciliation surface for trust + invoice
// payments — direct bank-feed APIs are deliberately out of scope.
//
// Flow: pick a CSV (any encoding the browser can decode as text) -> map
// columns (date / description / amount / reference) -> upload -> drill into
// rows -> match each credit to either a paid invoice or a trust deposit.

const ZAR = (n: number | null | undefined) =>
  n == null ? '-' : new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);

export default function BankImports() {
  const [imports, setImports] = useState<BankCsvImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [openImportId, setOpenImportId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await bankCsvApi.listImports();
      setImports(r.imports);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="stack">
      <header>
        <div className="row">
          <div className="grow">
            <h1>Bank CSV imports</h1>
            <p className="muted">
              Upload a bank statement export to reconcile against invoice payments and trust deposits.
              Direct bank-feed integrations are out of scope — CSV is the only path here, and that's intentional.
            </p>
          </div>
          <button className="primary" onClick={() => setShowUpload(true)}>+ Upload CSV</button>
        </div>
      </header>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading…</div>
        ) : imports.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>No imports yet. Upload a bank CSV to start reconciling.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Account</th><th>Uploaded</th>
                <th style={{ textAlign: 'right' }}>Rows</th>
                <th style={{ textAlign: 'right' }}>Matched</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => (
                <tr key={String(imp.ROWID)}>
                  <td style={{ fontWeight: 500 }}>{imp.account_label}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{(imp.uploaded_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{imp.row_count}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{imp.matched_count} / {imp.row_count}</td>
                  <td><span className="pill">{imp.status.replace(/_/g, ' ')}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => setOpenImportId(String(imp.ROWID))}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={(id) => { setShowUpload(false); load(); setOpenImportId(id); }}
        />
      )}

      {openImportId && (
        <ImportRowsModal
          importId={openImportId}
          onClose={() => { setOpenImportId(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Upload modal ----------

type ParsedRow = string[];

function UploadModal({ onClose, onUploaded }: { onClose: () => void; onUploaded: (id: string) => void }) {
  const [accountLabel, setAccountLabel] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({
    transaction_date: '', description: '', amount_zar: '', reference: '',
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Naive CSV parser: split on newlines, then on commas. Handles quoted
  // values that contain commas. Sufficient for SA bank exports we've seen.
  const onFile = async (file: File) => {
    setError(null);
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
    if (!lines.length) { setError('CSV looks empty.'); return; }
    const all = lines.map(splitCsvLine);
    const head = all[0].map((h) => h.replace(/^"|"$/g, '').trim());
    const rows = all.slice(1);
    setHeaders(head);
    setParsed(rows);
    if (!accountLabel) setAccountLabel(file.name.replace(/\.[^.]+$/, ''));

    // Heuristic auto-map for common SA bank column names.
    const guess = (...candidates: string[]) =>
      head.find((h) => candidates.some((c) => h.toLowerCase().includes(c))) || '';
    setMapping({
      transaction_date: guess('date', 'transaction date', 'txn date'),
      description:      guess('description', 'narrative', 'detail', 'reference'),
      amount_zar:       guess('amount', 'value', 'credit', 'debit'),
      reference:        guess('reference', 'ref'),
    });
  };

  const upload = async () => {
    if (!accountLabel.trim()) { setError('Account label is required.'); return; }
    if (!mapping.amount_zar)  { setError('Pick which column holds the amount.'); return; }
    setUploading(true); setError(null);
    try {
      const idxOf = (key: string) => mapping[key] ? headers.indexOf(mapping[key]) : -1;
      const dateIdx = idxOf('transaction_date');
      const descIdx = idxOf('description');
      const amtIdx  = idxOf('amount_zar');
      const refIdx  = idxOf('reference');

      const rows = parsed.map((cells, i) => ({
        row_num: i + 1,
        transaction_date: dateIdx >= 0 ? normaliseDate(cells[dateIdx]) : undefined,
        description: descIdx >= 0 ? (cells[descIdx] || '').replace(/^"|"$/g, '') : undefined,
        amount_zar: amtIdx >= 0 ? Number(String(cells[amtIdx] || '').replace(/[^\d.\-]/g, '')) : 0,
        reference: refIdx >= 0 ? (cells[refIdx] || '').replace(/^"|"$/g, '') : undefined,
      }));
      const r = await bankCsvApi.createImport({
        account_label: accountLabel.trim(),
        mapping,
        rows,
      });
      onUploaded(r.id);
    } catch (e) {
      setError((e as Error).message);
    } finally { setUploading(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={{ ...modalCard, width: 720 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Upload bank CSV</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
          The file is parsed in your browser. We send normalised rows to the server — never the raw file.
        </p>

        <div className="stack">
          <div>
            <label>Account label</label>
            <input value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)} placeholder="e.g. ABSA Trust 1234" />
          </div>

          <div>
            <label>CSV file</label>
            <input type="file" accept=".csv,text/csv" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
          </div>

          {headers.length > 0 && (
            <>
              <div>
                <label>Column mapping ({parsed.length} data row{parsed.length === 1 ? '' : 's'})</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  {(['transaction_date', 'description', 'amount_zar', 'reference'] as const).map((key) => (
                    <div key={key}>
                      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                        {key.replace(/_/g, ' ')}
                      </div>
                      <Select
                        value={mapping[key]}
                        onChange={(v) => setMapping({ ...mapping, [key]: v })}
                        options={[{ value: '', label: '(skip)' }, ...headers.map((h) => ({ value: h, label: h }))]}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Preview (first 5 rows)
                </div>
                <div className="card" style={{ padding: 0, overflow: 'auto', maxHeight: 200 }}>
                  <table style={{ fontSize: 11 }}>
                    <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                    <tbody>
                      {parsed.slice(0, 5).map((r, i) => (
                        <tr key={i}>
                          {r.map((c, j) => <td key={j} style={{ whiteSpace: 'nowrap' }}>{c.replace(/^"|"$/g, '')}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={uploading}>Cancel</button>
            <button className="primary" onClick={upload} disabled={uploading || parsed.length === 0}>
              {uploading ? 'Uploading…' : `Upload ${parsed.length} rows`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Rows modal (per import) ----------

function ImportRowsModal({ importId, onClose }: { importId: string; onClose: () => void }) {
  const [rows, setRows] = useState<BankCsvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchingRow, setMatchingRow] = useState<BankCsvRow | null>(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await bankCsvApi.listRows(importId)).rows); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [importId]);

  const unmatch = async (r: BankCsvRow) => {
    if (!confirm('Unlink this row from its match? The payment / trust deposit row stays in place.')) return;
    await bankCsvApi.unmatchRow(r.ROWID);
    await load();
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={{ ...modalCard, width: 1100, maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 10 }}>
          <h3 className="grow" style={{ margin: 0 }}>Bank CSV rows</h3>
          <button onClick={onClose}>Close</button>
        </div>

        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>No rows on this import.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Date</th>
                <th>Description</th>
                <th>Reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Matched</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={String(r.ROWID)}>
                  <td className="muted">{r.row_num}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.transaction_date || '—'}</td>
                  <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{r.reference || '—'}</td>
                  <td style={{
                    textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                    color: r.amount_zar > 0 ? 'var(--color-success)' : 'var(--color-text)',
                    fontWeight: 600,
                  }}>{ZAR(r.amount_zar)}</td>
                  <td>
                    {r.matched_kind ? (
                      <span className="pill success">{r.matched_kind === 'payment' ? 'Payment' : 'Trust deposit'}</span>
                    ) : (
                      <span className="muted" style={{ fontSize: 12 }}>unmatched</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {r.matched_kind ? (
                      <button className="ghost" style={{ color: 'var(--color-danger)' }} onClick={() => unmatch(r)}>Unmatch</button>
                    ) : r.amount_zar > 0 ? (
                      <button onClick={() => setMatchingRow(r)}>Match</button>
                    ) : (
                      <span className="muted" style={{ fontSize: 11 }}>(debit — skip)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {matchingRow && (
        <MatchModal
          row={matchingRow}
          onClose={() => setMatchingRow(null)}
          onMatched={() => { setMatchingRow(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Match modal (per row) ----------

function MatchModal({ row, onClose, onMatched }: { row: BankCsvRow; onClose: () => void; onMatched: () => void }) {
  const [kind, setKind] = useState<'payment' | 'trust_deposit'>('payment');
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [target, setTarget] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-load both options sets when the modal opens — small enough lists
  // that we don't need server-side search yet.
  useEffect(() => {
    invoiceApi.list({ status: 'issued' }).then((r) => setInvoices(r.invoices)).catch(() => null);
    invoiceApi.list({ status: 'partially_paid' }).then((r) => setInvoices((prev) => [...prev, ...r.invoices])).catch(() => null);
    matterApi.list({}).then((r) => setMatters(r.matters)).catch(() => null);
  }, []);

  const matterById = useMemo(() => {
    const m = new Map<string, MatterSummary>();
    matters.forEach((x) => m.set(String(x.ROWID), x));
    return m;
  }, [matters]);

  const submit = async () => {
    if (!target) { setError('Pick a target.'); return; }
    setBusy(true); setError(null);
    try {
      if (kind === 'payment') {
        await bankCsvApi.matchRow(row.ROWID, { kind: 'payment', target: { invoice_id: target } });
      } else {
        await bankCsvApi.matchRow(row.ROWID, { kind: 'trust_deposit', target: { matter_id: target } });
      }
      onMatched();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ ...backdrop, zIndex: 110 }} onClick={onClose}>
      <div className="card" style={{ ...modalCard, width: 620 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Match row to…</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {row.transaction_date} · {ZAR(row.amount_zar)} · {row.description || '—'}
        </p>

        <div className="stack">
          <div>
            <label>Match as</label>
            <Select
              value={kind}
              onChange={(v) => { setKind(v as 'payment' | 'trust_deposit'); setTarget(''); }}
              options={[
                { value: 'payment',       label: 'Payment against an invoice' },
                { value: 'trust_deposit', label: 'Trust deposit on a matter' },
              ]}
            />
          </div>

          {kind === 'payment' ? (
            <div>
              <label>Invoice</label>
              <Select
                value={target}
                onChange={setTarget}
                searchPlaceholder="Search invoices…"
                options={invoices
                  .filter((i) => Number(i.total_zar) - Number(i.amount_paid_zar || 0) > 0)
                  .map((i) => ({
                    value: String(i.ROWID),
                    label: `${i.number} · ${ZAR(Number(i.total_zar) - Number(i.amount_paid_zar || 0))} outstanding`,
                  }))}
              />
            </div>
          ) : (
            <div>
              <label>Matter</label>
              <Select
                value={target}
                onChange={setTarget}
                searchPlaceholder="Search matters…"
                options={matters.map((m) => ({
                  value: String(m.ROWID),
                  label: `${m.reference || `#${m.ROWID}`} — ${m.matter_name}`,
                }))}
              />
            </div>
          )}

          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={busy}>Cancel</button>
            <button className="primary" onClick={submit} disabled={busy || !target}>
              {busy ? 'Matching…' : 'Match'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- helpers ----------

// Minimal CSV line splitter — handles "quoted, values" without dragging in
// papaparse for one screen.
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

// Best-effort date normalisation: ISO passes through; common SA "DD/MM/YYYY"
// gets converted; everything else returns null and the row keeps no date.
function normaliseDate(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim().replace(/^"|"$/g, '');
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m) {
    const [, d, mo, y] = m;
    const yyyy = y.length === 2 ? `20${y}` : y;
    return `${yyyy}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return undefined;
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 620, maxWidth: '94vw' };
